'use client';

import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Button } from '@/react/components/core-ui/Button';
import { Icon, IconTypes } from '@/react/components/core-ui/Icon';
import { Image } from '@/react/components/core-ui/Image';
import { Tooltip } from '@/react/components/core-ui/Tooltip';
import ScriptLoader from '@/react/components/utility/ScriptLoader';
import { useConfig } from '@/react/hooks/useConfig';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import type { IProductVM } from '@/react/view-models/ProductVM';
import DisplayVariationAttributeType from '@/react/view-models/ProductVM/DisplayVariationAttributeType';
import LoggerService from '@/services/isomorphic/LoggerService';

import { useArtlabsAvailability } from '../hooks/useArtlabsAvailability';
import { useArtlabsConfig } from '../hooks/useArtlabsConfig';
import { getArtlabsConfigImages } from './internal/getArtlabsConfigImages';
import { getArtlabsStyleColorKeys } from './internal/getArtlabsStyleColorKeys';

import S from './styles.base.module.scss';
import { product_details_artlabs_title } from "@/lang/__generated__/ahnu/product_details_artlabs_title";
import { product_details_artlabs_tooltip } from "@/lang/__generated__/ahnu/product_details_artlabs_tooltip";
import { product_details_artlabs_tryOn } from "@/lang/__generated__/ahnu/product_details_artlabs_tryOn";
import { product_details_artlabs_viewInAr } from "@/lang/__generated__/ahnu/product_details_artlabs_viewInAr";

/**
 * Props for the ArtlabsExperienceButtons component.
 */
export interface IArtlabsExperienceButtonsProps {
  /**
   * The product to display the Artlabs buttons for.
   */
  product: IProductVM;
}

/**
 * Props for the ArtlabsButtonsContent component.
 */
export interface IArtlabsButtonsContentProps {
  /**
   * The product to display the Artlabs buttons for.
   */
  product: IProductVM;
}

/**
 * Injects the Artlabs Experience SDK script and initializes it using config from `useArtlabsConfig`.
 * Renders the 'View in AR' and 'Try-On' buttons when enabled and available for the current styleColor.
 * When Artlabs is disabled in config, renders nothing (no script, no UI).
 */
export const ArtlabsExperienceButtons = observer(
  function ArtlabsExperienceButtons({
    product
  }: IArtlabsExperienceButtonsProps) {
    const [cdnUrl, initConfig, enabled] = useArtlabsConfig();
    // A boolean to track if the Artlabs Experience SDK is ready to be used. This is used to
    // prevent the buttons from being rendered before the SDK is ready.
    const [isReady, setIsReady] = useState(false);

    const handleScriptReady = useCallback((): void => {
      void (async () => {
        try {
          const api = window.ArtlabsExperience;
          if (!api) {
            throw new Error(
              'Artlabs Experience SDK not available on window after script onReady'
            );
          }
          await api.init(initConfig);
          setIsReady(true);
        } catch (error) {
          LoggerService.error(
            new Error('Error initializing Artlabs Experience SDK.', {
              cause: error
            })
          );
        }
      })();
    }, [initConfig]);

    if (!enabled) {
      return null;
    }

    return (
      <>
        <ScriptLoader
          id="artlabs-experience"
          src={cdnUrl}
          strategy="afterInteractive"
          onReady={handleScriptReady}
        />
        {isReady && <ArtlabsButtonsContent product={product} />}
      </>
    );
  }
);

/**
 * Renders the 'View in AR' and 'Try-On' buttons when available for the current styleColor.
 * Renders nothing if the current styleColor is not available for AR or Try-On.
 */
export const ArtlabsButtonsContent = observer(function ArtlabsButtonsContent({
  product
}: IArtlabsButtonsContentProps) {
  const [msg] = useLocaleMessage();

  const selectedColorRaw =
    product.selectedVariations[DisplayVariationAttributeType.Color];
  const selectedColor = selectedColorRaw ?? '';
  const availableColorAttributes = product.getVariationAttributesByType(
    DisplayVariationAttributeType.Color
  );
  const { styleColor, allStyleColors } = getArtlabsStyleColorKeys({
    styleNumber: product.styleNumber,
    selectedColor,
    availableColorAttributes
  });

  const { availability, isFetching } = useArtlabsAvailability(styleColor);

  const artlabsConfig = useConfig('artlabs');
  const arIcon = artlabsConfig.getSetting('arIcon').value;
  const vtoIcon = artlabsConfig.getSetting('vtoIcon').value;
  const images = useMemo(
    () => getArtlabsConfigImages(arIcon, vtoIcon),
    [arIcon, vtoIcon]
  );

  const showAr = availability.ar;
  const showVto = availability.vto;

  if (!showAr && !showVto) return null;

  const handleViewInAr = (): void => {
    if (styleColor) {
      window.ArtlabsExperience?.startAR?.(styleColor, {});
    }
  };

  const handleTryOn = (): void => {
    if (allStyleColors) {
      window.ArtlabsExperience?.startVTO?.(allStyleColors);
    }
  };

  return (
    <div className={S.container}>
      <div className={S.title}>
        {msg(product_details_artlabs_title)}
        <span className={S.tooltip}>
          <Tooltip
            body={msg(product_details_artlabs_tooltip)}
            toggleOnClick
            position="down"
            variant="light"
            wrap
          >
            <Icon icon={IconTypes.CircleInfoLight} />
          </Tooltip>
        </span>
      </div>
      <div className={S.buttonsContainer} aria-busy={isFetching}>
        {showVto && (
          <Button
            variant="transparent-dark"
            onClick={handleTryOn}
            className={S.button}
            disabled={isFetching}
          >
            {images.vto.src ? (
              <Image
                image={images.vto}
                width={images.vto.width ?? 16}
                height={images.vto.height ?? 16}
                fill={false}
                useNext
                useSkeletonLoader={false}
                fit="contain"
                className={S.buttonIcon}
              />
            ) : null}
            {msg(product_details_artlabs_tryOn)}
          </Button>
        )}
        {showAr && (
          <Button
            variant="transparent-dark"
            onClick={handleViewInAr}
            className={S.button}
            disabled={isFetching}
          >
            {images.ar.src ? (
              <Image
                image={images.ar}
                width={images.ar.width ?? 16}
                height={images.ar.height ?? 16}
                fill={false}
                useNext
                useSkeletonLoader={false}
                fit="contain"
                className={S.buttonIcon}
              />
            ) : null}
            {msg(product_details_artlabs_viewInAr)}
          </Button>
        )}
      </div>
    </div>
  );
});
